import type { AllowedRoles, UserRole } from '@src/models/interfaces/access.interface';

import { resolveOrgAccess } from './packedClaims';
import { registerWriteRoleLists } from './writeRoles';

export type { AccessAction, AllowedRoles, UserRole } from '@src/models/interfaces/access.interface';
export {
  filterOrganizationsByAccess,
  hasOrgMembership,
  isPackedClaims,
  resolveOrgAccess,
} from './packedClaims';
export type { PackableClaims } from './packedClaims';
export { isWriteRoleList, mergeAllowedRoles } from './writeRoles';
export {
  canRunPlatformAction,
  PLATFORM_ACTION_ROLES,
  PLATFORM_CLAIM_KEY,
  resolvePlatformRole,
} from './platform';
export type { PlatformClaims } from './platform';

export const ACCESS_TYPES = {
  // Org access only
  OWNER: 'o',
  // Div access only
  EDITOR: 'e',
  // Both
  ADMIN: 'a',
  READER: 'r',
  USER: 'u',
} as const;

export type OrgAccessCode = (typeof ACCESS_TYPES)[keyof typeof ACCESS_TYPES];

/**
 * Higher rank = more privilege. Used for sensitive admin actions
 * (password/email) so peers and superiors cannot be managed.
 */
const ORG_ACCESS_RANK: Partial<Record<OrgAccessCode, number>> = {
  [ACCESS_TYPES.OWNER]: 3,
  [ACCESS_TYPES.ADMIN]: 2,
  [ACCESS_TYPES.READER]: 1,
  [ACCESS_TYPES.USER]: 0,
};

/**
 * Returns true when requesterAccess is strictly above targetAccess.
 * Peers (e.g. Admin vs Admin) and missing ranks do not outrank.
 */
export const outranksOrgAccess = (
  requesterAccess?: string | null,
  targetAccess?: string | null,
): boolean => {
  if (!requesterAccess || !targetAccess) return false;
  const requesterRank = ORG_ACCESS_RANK[requesterAccess as OrgAccessCode];
  const targetRank = ORG_ACCESS_RANK[targetAccess as OrgAccessCode];
  if (requesterRank === undefined || targetRank === undefined) return false;
  return requesterRank > targetRank;
};

/**
 * Whether the requester may manage sensitive actions on the target (password/email).
 * Own account is always allowed; otherwise requires strictly higher org access.
 */
export const canManageOrgUser = (
  requesterId?: string | null,
  requesterAccess?: string | null,
  targetId?: string | null,
  targetAccess?: string | null,
): boolean => {
  if (requesterId && targetId && requesterId === targetId) return true;
  return outranksOrgAccess(requesterAccess, targetAccess);
};

export const ACCESS_TYPES_INTL = {
  [ACCESS_TYPES.OWNER]: {
    desc: 'accessType.owner.description',
    name: 'accessType.owner',
  },
  [ACCESS_TYPES.ADMIN]: {
    desc: 'accessType.admin.description',
    name: 'accessType.admin',
  },
  [ACCESS_TYPES.EDITOR]: {
    desc: 'accessType.editor.description',
    name: 'accessType.editor',
  },
  [ACCESS_TYPES.READER]: {
    desc: 'accessType.reader.description',
    name: 'accessType.reader',
  },
  [ACCESS_TYPES.USER]: {
    desc: 'accessType.user.description',
    name: 'accessType.user',
  },
};

// To the dev: if you add a new role here
// you should update "getUserRoles" and "ALLOWED_ROLES"
export const USER_ROLES = {
  OWNER: 'owner',
  ORG_ADMIN: 'orgAdmin',
  ORG_READER: 'orgReader',
  ORG_USER: 'orgUser',
  // ----------------------
  // The following are combinations of the "u" or "r" org access, with div access
  DIV_ADMIN: 'divAdmin',
  DIV_READER: 'divReader',
  DIV_EDITOR: 'divEditor',
  DIV_USER: 'divUser',
} as const;

const ALL_USER_ROLES = Object.values(USER_ROLES) as UserRole[];
// User that doesnt have any of the above accesses
const OUTSIDER: UserRole = 'outsider';

// Possible actions to use with firestore
const ACTIONS = {
  CREATE: 'CREATE',
  UPDATE: 'UPDATE',
  DELETE: 'DELETE',
  GET: 'GET',
  LIST: 'LIST',
};

type ClaimsWithOrg = { org?: Record<string, string | undefined> };

// Owners and Admins only have/need the ORG access
// Users only have/need the division access (check?)
// But Readers have 2 access levels, the one at the DIV and the one at the ORG
// Case 1: to read a X doc you need to be ORG_ADMIN or DIV_ADMIN at div Y
// - User has: org access: Reader; div Y access: Admin
// - has enought div access, but not org access. Result: he should be able to read the X doc.
// Case 2: to read a X doc you need to be ORG_READER or DIV_ADMIN at div Y
// - User has: org access: Reader; div Y access: User
// - has enought org access, but not div access. Result: he should be able to read the X doc.
//
// Both access levels should be taken in consideration,
// thats why ORG_READERS return 2 "userRoles"
const getUserRoles = (orgAccess = '', divAccess = ''): UserRole[] => {
  const divUserRoles = {
    [ACCESS_TYPES.ADMIN]: USER_ROLES.DIV_ADMIN,
    [ACCESS_TYPES.EDITOR]: USER_ROLES.DIV_EDITOR,
    [ACCESS_TYPES.READER]: USER_ROLES.DIV_READER,
    [ACCESS_TYPES.USER]: USER_ROLES.DIV_USER,
  };

  switch (orgAccess) {
    case ACCESS_TYPES.OWNER:
      return [USER_ROLES.OWNER];
    case ACCESS_TYPES.ADMIN:
      return [USER_ROLES.ORG_ADMIN];
    case ACCESS_TYPES.READER:
      return [divUserRoles[divAccess], USER_ROLES.ORG_READER] as UserRole[];
    case ACCESS_TYPES.USER:
      return [divUserRoles[divAccess], USER_ROLES.ORG_USER] as UserRole[];
    default:
      return [OUTSIDER];
  }
};

// Returns an arry of only those divisions that meets any of the required "allowedRoles"
export const getDivsWithAccess = <T extends { id?: string; access?: string }>(
  allowedRoles: readonly string[],
  orgAccess: string | undefined,
  userDivs?: Record<string, T> | null,
): T[] => {
  const allowedDivs = Object.values(userDivs || {}).filter(({ access }) => {
    const userRoles = getUserRoles(orgAccess, access);
    return userRoles.some(role => allowedRoles.includes(role));
  });

  return allowedDivs;
};

// If ANY of those types meets any of the required "allowedRoles"
// then the user has access
export const hasAnyAllowedRole = (
  allowedRoles: readonly string[] | null | undefined,
  orgAccess: string | undefined,
  userDivs: Record<string, { access?: string }> = {},
): boolean => {
  const allUserRoles: UserRole[] = [];
  Object.values(userDivs || {}).forEach(({ access }) => {
    const userRoles = getUserRoles(orgAccess, access);
    allUserRoles.push(...userRoles);
  });
  allUserRoles.push(...getUserRoles(orgAccess));

  return allowedRoles ? allUserRoles.some(role => allowedRoles.includes(role)) : true;
};

const ORG_WIDE_ACCESS: string[] = [ACCESS_TYPES.OWNER, ACCESS_TYPES.ADMIN, ACCESS_TYPES.READER];

/**
 * Whether the org role alone grants access to every division of the organization,
 * without per-division membership. Mirrors the `orgAccess` list the backend
 * checks in TimeTrackerAccessService.
 */
export const hasOrgWideAccess = (orgAccess?: string | null): boolean =>
  !!orgAccess && ORG_WIDE_ACCESS.includes(orgAccess);

/**
 * Narrows `ALLOWED_ROLES.*` arrays from inferred `string[]` to `AllowedRoles`.
 * Runtime value is unchanged.
 */
export function asAllowedRoles(roles: readonly string[]): AllowedRoles {
  return roles as AllowedRoles;
}

// Allowed firebase actions by entity and user access
// Firebase collections/entities organized with their corresponding nested entities
// To the dev: this is an attempt to mirror how the accesses permissions where organizated and declared at "Services" project.
// You should check the "generic.test.ts" files to keep these Actions updated. In case of a new ACCESS_TYPE being added you
// should map all the corresponding actions access he has for each entity.
// If new entities are added to the "Services" project this should be updated
// Creating a new organization stays open in read-only mode: the new org gets its own account.
const ORGANIZATION_CREATE_ROLES: UserRole[] = [...ALL_USER_ROLES, OUTSIDER];

export const ALLOWED_ROLES = {
  ORGANIZATIONS: {
    [ACTIONS.CREATE]: ORGANIZATION_CREATE_ROLES,
    [ACTIONS.UPDATE]: [USER_ROLES.OWNER, USER_ROLES.ORG_ADMIN],
    [ACTIONS.DELETE]: [],
    [ACTIONS.GET]: [USER_ROLES.OWNER, USER_ROLES.ORG_ADMIN, USER_ROLES.ORG_READER],
    [ACTIONS.LIST]: [],
    OPERATIONAL_PANEL: {
      OPERATION: [USER_ROLES.OWNER, USER_ROLES.ORG_ADMIN, USER_ROLES.DIV_ADMIN],
    },
    // Home is every member's landing screen: the route carries no gate and the
    // panels inside it scope their own data by role. Open to the whole org on
    // purpose — outsiders are still excluded (`ALL_USER_ROLES` has no OUTSIDER).
    HOME: {
      [ACTIONS.LIST]: ALL_USER_ROLES,
    },
    AUDIT_LOGS: {
      [ACTIONS.CREATE]: [],
      [ACTIONS.UPDATE]: [],
      [ACTIONS.DELETE]: [],
      [ACTIONS.GET]: [USER_ROLES.OWNER, USER_ROLES.ORG_ADMIN],
      [ACTIONS.LIST]: [USER_ROLES.OWNER, USER_ROLES.ORG_ADMIN],
    },
    USER_IMPORTS: {
      [ACTIONS.CREATE]: [USER_ROLES.OWNER, USER_ROLES.ORG_ADMIN],
      [ACTIONS.UPDATE]: [USER_ROLES.OWNER, USER_ROLES.ORG_ADMIN],
      [ACTIONS.DELETE]: [USER_ROLES.OWNER, USER_ROLES.ORG_ADMIN],
      [ACTIONS.GET]: [USER_ROLES.OWNER, USER_ROLES.ORG_ADMIN],
      [ACTIONS.LIST]: [USER_ROLES.OWNER, USER_ROLES.ORG_ADMIN],
    },
    DIVISIONS: {
      [ACTIONS.CREATE]: [USER_ROLES.OWNER, USER_ROLES.ORG_ADMIN],
      [ACTIONS.UPDATE]: [
        USER_ROLES.OWNER,
        USER_ROLES.ORG_ADMIN,
        USER_ROLES.DIV_ADMIN,
        USER_ROLES.DIV_EDITOR,
      ],
      [ACTIONS.DELETE]: [USER_ROLES.OWNER, USER_ROLES.ORG_ADMIN],
      [ACTIONS.GET]: [
        USER_ROLES.OWNER,
        USER_ROLES.ORG_ADMIN,
        USER_ROLES.ORG_READER,
        USER_ROLES.DIV_ADMIN,
        USER_ROLES.DIV_EDITOR,
        USER_ROLES.DIV_READER,
      ],
      [ACTIONS.LIST]: [USER_ROLES.OWNER, USER_ROLES.ORG_ADMIN, USER_ROLES.ORG_READER],
      EVENT_TYPES: {
        [ACTIONS.CREATE]: [
          USER_ROLES.OWNER,
          USER_ROLES.ORG_ADMIN,
          USER_ROLES.DIV_ADMIN,
          USER_ROLES.DIV_EDITOR,
        ],
        [ACTIONS.UPDATE]: [
          USER_ROLES.OWNER,
          USER_ROLES.ORG_ADMIN,
          USER_ROLES.DIV_ADMIN,
          USER_ROLES.DIV_EDITOR,
        ],
        [ACTIONS.DELETE]: [USER_ROLES.OWNER, USER_ROLES.ORG_ADMIN, USER_ROLES.DIV_ADMIN],
        // EVENT_TYPES GET/LIST: no DIV_USER (backend rejects; avoid client "User not authorized")
        [ACTIONS.GET]: [
          USER_ROLES.OWNER,
          USER_ROLES.ORG_ADMIN,
          USER_ROLES.ORG_READER,
          USER_ROLES.DIV_ADMIN,
          USER_ROLES.DIV_EDITOR,
          USER_ROLES.DIV_READER,
        ],
        [ACTIONS.LIST]: [
          USER_ROLES.OWNER,
          USER_ROLES.ORG_ADMIN,
          USER_ROLES.ORG_READER,
          USER_ROLES.DIV_ADMIN,
          USER_ROLES.DIV_EDITOR,
          USER_ROLES.DIV_READER,
        ],
      },
      EVENTS: {
        // Copies, not the shared list: write lists are registered by identity for the
        // read-only account gate (see `writeRoles.ts`), and GET/LIST reuse ALL_USER_ROLES.
        [ACTIONS.CREATE]: [...ALL_USER_ROLES],
        [ACTIONS.UPDATE]: [...ALL_USER_ROLES],
        [ACTIONS.DELETE]: [USER_ROLES.OWNER, USER_ROLES.ORG_ADMIN, USER_ROLES.DIV_ADMIN],
        [ACTIONS.GET]: ALL_USER_ROLES,
        [ACTIONS.LIST]: ALL_USER_ROLES,
      },
      EVENTS_SUMMARY: {
        [ACTIONS.LIST]: ALL_USER_ROLES,
      },
      EXECUTIONS: {
        [ACTIONS.CREATE]: [],
        [ACTIONS.UPDATE]: [USER_ROLES.DIV_USER],
        [ACTIONS.DELETE]: [],
        [ACTIONS.GET]: ALL_USER_ROLES,
        [ACTIONS.LIST]: [
          USER_ROLES.OWNER,
          USER_ROLES.ORG_ADMIN,
          USER_ROLES.ORG_READER,
          USER_ROLES.DIV_ADMIN,
          USER_ROLES.DIV_READER,
          USER_ROLES.DIV_EDITOR,
        ],
      },
      // Schedules, Routes, Tasks and Operations reports. Mirrors Services `search.schedules`,
      // `search.executionsHistoryDashboard` and `reports.generatePdf`: org o/a/r + REPORTS_DIV_ACCESS.
      REPORTS: {
        [ACTIONS.LIST]: [
          USER_ROLES.OWNER,
          USER_ROLES.ORG_ADMIN,
          USER_ROLES.ORG_READER,
          USER_ROLES.DIV_ADMIN,
          USER_ROLES.DIV_READER,
          USER_ROLES.DIV_EDITOR,
        ],
      },
      GROUPS: {
        [ACTIONS.CREATE]: [
          USER_ROLES.OWNER,
          USER_ROLES.ORG_ADMIN,
          USER_ROLES.DIV_ADMIN,
          USER_ROLES.DIV_EDITOR,
        ],
        [ACTIONS.UPDATE]: [
          USER_ROLES.OWNER,
          USER_ROLES.ORG_ADMIN,
          USER_ROLES.DIV_ADMIN,
          USER_ROLES.DIV_EDITOR,
        ],
        [ACTIONS.DELETE]: [USER_ROLES.OWNER, USER_ROLES.ORG_ADMIN, USER_ROLES.DIV_ADMIN],
        [ACTIONS.GET]: [
          USER_ROLES.OWNER,
          USER_ROLES.ORG_ADMIN,
          USER_ROLES.ORG_READER,
          USER_ROLES.DIV_ADMIN,
          USER_ROLES.DIV_READER,
          USER_ROLES.DIV_EDITOR,
        ],
        [ACTIONS.LIST]: [
          USER_ROLES.OWNER,
          USER_ROLES.ORG_ADMIN,
          USER_ROLES.ORG_READER,
          USER_ROLES.DIV_ADMIN,
          USER_ROLES.DIV_READER,
          USER_ROLES.DIV_EDITOR,
        ],
      },
      LOCATIONS: {
        [ACTIONS.CREATE]: [
          USER_ROLES.OWNER,
          USER_ROLES.ORG_ADMIN,
          USER_ROLES.DIV_ADMIN,
          USER_ROLES.DIV_EDITOR,
        ],
        [ACTIONS.UPDATE]: [
          USER_ROLES.OWNER,
          USER_ROLES.ORG_ADMIN,
          USER_ROLES.DIV_ADMIN,
          USER_ROLES.DIV_EDITOR,
        ],
        [ACTIONS.DELETE]: [
          USER_ROLES.OWNER,
          USER_ROLES.ORG_ADMIN,
          USER_ROLES.DIV_ADMIN,
          USER_ROLES.DIV_EDITOR,
        ],
        [ACTIONS.GET]: [
          USER_ROLES.OWNER,
          USER_ROLES.ORG_ADMIN,
          USER_ROLES.ORG_READER,
          USER_ROLES.DIV_ADMIN,
          USER_ROLES.DIV_READER,
          USER_ROLES.DIV_EDITOR,
        ],
        [ACTIONS.LIST]: [
          USER_ROLES.OWNER,
          USER_ROLES.ORG_ADMIN,
          USER_ROLES.ORG_READER,
          USER_ROLES.DIV_ADMIN,
          USER_ROLES.DIV_READER,
          USER_ROLES.DIV_EDITOR,
        ],
      },
      LOCATION_GROUPS: {
        [ACTIONS.CREATE]: [
          USER_ROLES.OWNER,
          USER_ROLES.ORG_ADMIN,
          USER_ROLES.DIV_ADMIN,
          USER_ROLES.DIV_EDITOR,
        ],
        [ACTIONS.UPDATE]: [
          USER_ROLES.OWNER,
          USER_ROLES.ORG_ADMIN,
          USER_ROLES.DIV_ADMIN,
          USER_ROLES.DIV_EDITOR,
        ],
        [ACTIONS.DELETE]: [
          USER_ROLES.OWNER,
          USER_ROLES.ORG_ADMIN,
          USER_ROLES.DIV_ADMIN,
          USER_ROLES.DIV_EDITOR,
        ],
        [ACTIONS.GET]: [
          USER_ROLES.OWNER,
          USER_ROLES.ORG_ADMIN,
          USER_ROLES.ORG_READER,
          USER_ROLES.DIV_ADMIN,
          USER_ROLES.DIV_READER,
          USER_ROLES.DIV_EDITOR,
        ],
        [ACTIONS.LIST]: [
          USER_ROLES.OWNER,
          USER_ROLES.ORG_ADMIN,
          USER_ROLES.ORG_READER,
          USER_ROLES.DIV_ADMIN,
          USER_ROLES.DIV_READER,
          USER_ROLES.DIV_EDITOR,
        ],
      },
      SCHEDULES: {
        [ACTIONS.CREATE]: [
          USER_ROLES.OWNER,
          USER_ROLES.ORG_ADMIN,
          USER_ROLES.DIV_ADMIN,
          USER_ROLES.DIV_EDITOR,
        ],
        [ACTIONS.UPDATE]: [
          USER_ROLES.OWNER,
          USER_ROLES.ORG_ADMIN,
          USER_ROLES.DIV_ADMIN,
          USER_ROLES.DIV_EDITOR,
        ],
        [ACTIONS.DELETE]: [
          USER_ROLES.OWNER,
          USER_ROLES.ORG_ADMIN,
          USER_ROLES.DIV_ADMIN,
          USER_ROLES.DIV_EDITOR,
        ],
        [ACTIONS.GET]: [
          USER_ROLES.OWNER,
          USER_ROLES.ORG_ADMIN,
          USER_ROLES.DIV_ADMIN,
          USER_ROLES.DIV_EDITOR,
        ],
        [ACTIONS.LIST]: [
          USER_ROLES.OWNER,
          USER_ROLES.ORG_ADMIN,
          USER_ROLES.ORG_READER,
          USER_ROLES.DIV_ADMIN,
          USER_ROLES.DIV_READER,
          USER_ROLES.DIV_EDITOR,
        ],
      },
      TRIGGERS: {
        [ACTIONS.CREATE]: [
          USER_ROLES.OWNER,
          USER_ROLES.ORG_ADMIN,
          USER_ROLES.DIV_ADMIN,
          USER_ROLES.DIV_EDITOR,
        ],
        [ACTIONS.UPDATE]: [
          USER_ROLES.OWNER,
          USER_ROLES.ORG_ADMIN,
          USER_ROLES.DIV_ADMIN,
          USER_ROLES.DIV_EDITOR,
        ],
        [ACTIONS.DELETE]: [
          USER_ROLES.OWNER,
          USER_ROLES.ORG_ADMIN,
          USER_ROLES.DIV_ADMIN,
          USER_ROLES.DIV_EDITOR,
        ],
        [ACTIONS.GET]: [
          USER_ROLES.OWNER,
          USER_ROLES.ORG_ADMIN,
          USER_ROLES.DIV_ADMIN,
          USER_ROLES.DIV_EDITOR,
        ],
        [ACTIONS.LIST]: [
          USER_ROLES.OWNER,
          USER_ROLES.ORG_ADMIN,
          USER_ROLES.ORG_READER,
          USER_ROLES.DIV_ADMIN,
          USER_ROLES.DIV_READER,
          USER_ROLES.DIV_EDITOR,
        ],
      },
      USER_POSITION_HISTORY: {
        [ACTIONS.LIST]: [USER_ROLES.OWNER, USER_ROLES.ORG_ADMIN, USER_ROLES.DIV_ADMIN],
      },
      WORK_TEMPLATES: {
        [ACTIONS.CREATE]: [
          USER_ROLES.OWNER,
          USER_ROLES.ORG_ADMIN,
          USER_ROLES.DIV_ADMIN,
          USER_ROLES.DIV_EDITOR,
        ],
        [ACTIONS.UPDATE]: [
          USER_ROLES.OWNER,
          USER_ROLES.ORG_ADMIN,
          USER_ROLES.DIV_ADMIN,
          USER_ROLES.DIV_EDITOR,
        ],
        [ACTIONS.DELETE]: [
          USER_ROLES.OWNER,
          USER_ROLES.ORG_ADMIN,
          USER_ROLES.ORG_READER,
          USER_ROLES.DIV_ADMIN,
          USER_ROLES.DIV_READER,
          USER_ROLES.DIV_EDITOR,
        ],
        [ACTIONS.GET]: [
          USER_ROLES.OWNER,
          USER_ROLES.ORG_ADMIN,
          USER_ROLES.DIV_ADMIN,
          USER_ROLES.DIV_EDITOR,
        ],
        [ACTIONS.LIST]: [
          USER_ROLES.OWNER,
          USER_ROLES.ORG_ADMIN,
          USER_ROLES.ORG_READER,
          USER_ROLES.DIV_ADMIN,
          USER_ROLES.DIV_READER,
          USER_ROLES.DIV_EDITOR,
        ],
      },
      WORK_ORDER: {
        [ACTIONS.CREATE]: [
          USER_ROLES.OWNER,
          USER_ROLES.ORG_ADMIN,
          USER_ROLES.DIV_ADMIN,
          USER_ROLES.DIV_EDITOR,
        ],
        [ACTIONS.UPDATE]: [
          USER_ROLES.OWNER,
          USER_ROLES.ORG_ADMIN,
          USER_ROLES.DIV_ADMIN,
          USER_ROLES.DIV_EDITOR,
        ],
        [ACTIONS.DELETE]: [
          USER_ROLES.OWNER,
          USER_ROLES.ORG_ADMIN,
          USER_ROLES.ORG_READER,
          USER_ROLES.DIV_ADMIN,
          USER_ROLES.DIV_READER,
          USER_ROLES.DIV_EDITOR,
        ],
        [ACTIONS.GET]: [
          USER_ROLES.OWNER,
          USER_ROLES.ORG_ADMIN,
          USER_ROLES.DIV_ADMIN,
          USER_ROLES.DIV_EDITOR,
        ],
        [ACTIONS.LIST]: [
          USER_ROLES.OWNER,
          USER_ROLES.ORG_ADMIN,
          USER_ROLES.ORG_READER,
          USER_ROLES.DIV_ADMIN,
          USER_ROLES.DIV_READER,
          USER_ROLES.DIV_EDITOR,
        ],
      },
      WORK_JOURNEY: {
        [ACTIONS.CREATE]: [
          USER_ROLES.OWNER,
          USER_ROLES.ORG_ADMIN,
          USER_ROLES.DIV_ADMIN,
          USER_ROLES.DIV_EDITOR,
        ],
        [ACTIONS.UPDATE]: [
          USER_ROLES.OWNER,
          USER_ROLES.ORG_ADMIN,
          USER_ROLES.DIV_ADMIN,
          USER_ROLES.DIV_EDITOR,
        ],
        [ACTIONS.DELETE]: [
          USER_ROLES.OWNER,
          USER_ROLES.ORG_ADMIN,
          USER_ROLES.ORG_READER,
          USER_ROLES.DIV_ADMIN,
          USER_ROLES.DIV_READER,
          USER_ROLES.DIV_EDITOR,
        ],
        [ACTIONS.GET]: [
          USER_ROLES.OWNER,
          USER_ROLES.ORG_ADMIN,
          USER_ROLES.DIV_ADMIN,
          USER_ROLES.DIV_EDITOR,
        ],
        [ACTIONS.LIST]: [
          USER_ROLES.OWNER,
          USER_ROLES.ORG_ADMIN,
          USER_ROLES.ORG_READER,
          USER_ROLES.DIV_ADMIN,
          USER_ROLES.DIV_READER,
          USER_ROLES.DIV_EDITOR,
        ],
      },
      DASHBOARDS: {
        // Metrics module is admin-only for writes: owner, org admin and division
        // admin. DIV_EDITOR reads and filters boards, it does not author them.
        [ACTIONS.CREATE]: [USER_ROLES.OWNER, USER_ROLES.ORG_ADMIN, USER_ROLES.DIV_ADMIN],
        [ACTIONS.UPDATE]: [USER_ROLES.OWNER, USER_ROLES.ORG_ADMIN, USER_ROLES.DIV_ADMIN],
        [ACTIONS.DELETE]: [USER_ROLES.OWNER, USER_ROLES.ORG_ADMIN, USER_ROLES.DIV_ADMIN],
        [ACTIONS.GET]: ALL_USER_ROLES,
        [ACTIONS.LIST]: [
          USER_ROLES.OWNER,
          USER_ROLES.ORG_ADMIN,
          USER_ROLES.ORG_READER,
          USER_ROLES.DIV_ADMIN,
          USER_ROLES.DIV_READER,
          USER_ROLES.DIV_EDITOR,
        ],
        CHARTS: {
          [ACTIONS.CREATE]: [USER_ROLES.OWNER, USER_ROLES.ORG_ADMIN, USER_ROLES.DIV_ADMIN],
          [ACTIONS.UPDATE]: [USER_ROLES.OWNER, USER_ROLES.ORG_ADMIN, USER_ROLES.DIV_ADMIN],
          [ACTIONS.DELETE]: [USER_ROLES.OWNER, USER_ROLES.ORG_ADMIN, USER_ROLES.DIV_ADMIN],
          [ACTIONS.GET]: [USER_ROLES.OWNER, USER_ROLES.ORG_ADMIN, USER_ROLES.DIV_ADMIN],
          // Division scope of the chart query (useGetDivisionsAllowed). Mirrors
          // DIVISIONS.DASHBOARDS.LIST so a reader sees the same boards' data.
          [ACTIONS.LIST]: [
            USER_ROLES.OWNER,
            USER_ROLES.ORG_ADMIN,
            USER_ROLES.ORG_READER,
            USER_ROLES.DIV_ADMIN,
            USER_ROLES.DIV_EDITOR,
          ],
        },
      },
    },
    DASHBOARDS: {
      [ACTIONS.CREATE]: [USER_ROLES.OWNER, USER_ROLES.ORG_ADMIN, USER_ROLES.DIV_ADMIN],
      [ACTIONS.UPDATE]: [USER_ROLES.OWNER, USER_ROLES.ORG_ADMIN, USER_ROLES.DIV_ADMIN],
      [ACTIONS.DELETE]: [USER_ROLES.OWNER, USER_ROLES.ORG_ADMIN, USER_ROLES.DIV_ADMIN],
      [ACTIONS.GET]: ALL_USER_ROLES,
      [ACTIONS.LIST]: [USER_ROLES.OWNER, USER_ROLES.ORG_ADMIN, USER_ROLES.ORG_READER],
      CHARTS: {
        [ACTIONS.CREATE]: [USER_ROLES.OWNER, USER_ROLES.ORG_ADMIN, USER_ROLES.DIV_ADMIN],
        [ACTIONS.UPDATE]: [USER_ROLES.OWNER, USER_ROLES.ORG_ADMIN, USER_ROLES.DIV_ADMIN],
        [ACTIONS.DELETE]: [USER_ROLES.OWNER, USER_ROLES.ORG_ADMIN, USER_ROLES.DIV_ADMIN],
        [ACTIONS.GET]: ALL_USER_ROLES,
        // Route + sidebar gate for Metrics. ORG_READER consumes the module
        // read-only: every write above stays admin-only.
        [ACTIONS.LIST]: [
          USER_ROLES.OWNER,
          USER_ROLES.ORG_ADMIN,
          USER_ROLES.ORG_READER,
          USER_ROLES.DIV_ADMIN,
        ],
      },
    },
    DOCUMENT_REQUESTS: {
      [ACTIONS.CREATE]: [USER_ROLES.OWNER, USER_ROLES.ORG_ADMIN],
      [ACTIONS.UPDATE]: [USER_ROLES.OWNER, USER_ROLES.ORG_ADMIN],
      [ACTIONS.DELETE]: [],
      [ACTIONS.GET]: [USER_ROLES.OWNER, USER_ROLES.ORG_ADMIN, USER_ROLES.ORG_READER],
      [ACTIONS.LIST]: [USER_ROLES.OWNER, USER_ROLES.ORG_ADMIN, USER_ROLES.ORG_READER],
    },
    EVENTS_SUMMARY: {
      [ACTIONS.CREATE]: [],
      [ACTIONS.UPDATE]: [],
      [ACTIONS.DELETE]: [],
      [ACTIONS.GET]: ALL_USER_ROLES,
      [ACTIONS.LIST]: ALL_USER_ROLES,
    },
    // Org-wide forms remain writable only by organization roles. Division-scoped
    // forms use the DIVISIONS policy below and are checked against every stored id.
    EVENT_TYPES: {
      [ACTIONS.CREATE]: [USER_ROLES.OWNER, USER_ROLES.ORG_ADMIN],
      [ACTIONS.UPDATE]: [USER_ROLES.OWNER, USER_ROLES.ORG_ADMIN],
      [ACTIONS.DELETE]: [USER_ROLES.OWNER, USER_ROLES.ORG_ADMIN],
      [ACTIONS.GET]: [USER_ROLES.OWNER, USER_ROLES.ORG_ADMIN, USER_ROLES.ORG_READER],
      [ACTIONS.LIST]: [USER_ROLES.OWNER, USER_ROLES.ORG_ADMIN, USER_ROLES.ORG_READER],
    },
    // Org-wide trigger templates remain writable only by organization roles.
    TRIGGERS: {
      [ACTIONS.CREATE]: [USER_ROLES.OWNER, USER_ROLES.ORG_ADMIN],
      [ACTIONS.UPDATE]: [USER_ROLES.OWNER, USER_ROLES.ORG_ADMIN],
      [ACTIONS.DELETE]: [USER_ROLES.OWNER, USER_ROLES.ORG_ADMIN],
      [ACTIONS.GET]: [
        USER_ROLES.OWNER,
        USER_ROLES.ORG_ADMIN,
        USER_ROLES.ORG_READER,
        USER_ROLES.DIV_ADMIN,
        USER_ROLES.DIV_EDITOR,
        USER_ROLES.DIV_READER,
      ],
      [ACTIONS.LIST]: [
        USER_ROLES.OWNER,
        USER_ROLES.ORG_ADMIN,
        USER_ROLES.ORG_READER,
        USER_ROLES.DIV_ADMIN,
        USER_ROLES.DIV_EDITOR,
        USER_ROLES.DIV_READER,
      ],
    },
    EMERGENCIES: {
      [ACTIONS.CREATE]: [],
      [ACTIONS.UPDATE]: [USER_ROLES.OWNER, USER_ROLES.ORG_ADMIN, USER_ROLES.DIV_ADMIN],
      [ACTIONS.DELETE]: [],
      [ACTIONS.GET]: [
        USER_ROLES.OWNER,
        USER_ROLES.ORG_ADMIN,
        USER_ROLES.ORG_READER,
        USER_ROLES.DIV_ADMIN,
        USER_ROLES.DIV_EDITOR,
        USER_ROLES.DIV_READER,
      ],
      [ACTIONS.LIST]: [
        USER_ROLES.OWNER,
        USER_ROLES.ORG_ADMIN,
        USER_ROLES.ORG_READER,
        USER_ROLES.DIV_ADMIN,
        USER_ROLES.DIV_EDITOR,
        USER_ROLES.DIV_READER,
      ],
    },
    EMERGENCY_TYPES: {
      [ACTIONS.CREATE]: [USER_ROLES.OWNER, USER_ROLES.ORG_ADMIN, USER_ROLES.DIV_ADMIN],
      [ACTIONS.UPDATE]: [USER_ROLES.OWNER, USER_ROLES.ORG_ADMIN, USER_ROLES.DIV_ADMIN],
      [ACTIONS.DELETE]: [USER_ROLES.OWNER, USER_ROLES.ORG_ADMIN, USER_ROLES.DIV_ADMIN],
      [ACTIONS.GET]: [
        USER_ROLES.OWNER,
        USER_ROLES.ORG_ADMIN,
        USER_ROLES.ORG_READER,
        USER_ROLES.DIV_ADMIN,
        USER_ROLES.DIV_EDITOR,
        USER_ROLES.DIV_READER,
      ],
      [ACTIONS.LIST]: [
        USER_ROLES.OWNER,
        USER_ROLES.ORG_ADMIN,
        USER_ROLES.ORG_READER,
        USER_ROLES.DIV_ADMIN,
        USER_ROLES.DIV_EDITOR,
        USER_ROLES.DIV_READER,
      ],
    },
    EMERGENCY_INTEGRATIONS: {
      [ACTIONS.CREATE]: [USER_ROLES.OWNER, USER_ROLES.ORG_ADMIN],
      [ACTIONS.UPDATE]: [USER_ROLES.OWNER, USER_ROLES.ORG_ADMIN],
      [ACTIONS.DELETE]: [USER_ROLES.OWNER, USER_ROLES.ORG_ADMIN],
      [ACTIONS.GET]: [USER_ROLES.OWNER, USER_ROLES.ORG_ADMIN],
      [ACTIONS.LIST]: [USER_ROLES.OWNER, USER_ROLES.ORG_ADMIN],
    },
    INVITATIONS: {
      [ACTIONS.CREATE]: [USER_ROLES.OWNER, USER_ROLES.ORG_ADMIN],
      [ACTIONS.UPDATE]: [],
      [ACTIONS.DELETE]: [USER_ROLES.OWNER, USER_ROLES.ORG_ADMIN],
      [ACTIONS.GET]: [],
      [ACTIONS.LIST]: [USER_ROLES.OWNER, USER_ROLES.ORG_ADMIN],
    },
    LOCATIONS: {
      // Firestore requires isOrganizationAdmin to write under organizations/{org}/locations,
      // division roles only apply to the DIVISIONS.LOCATIONS matrix.
      [ACTIONS.CREATE]: [USER_ROLES.OWNER, USER_ROLES.ORG_ADMIN],
      [ACTIONS.UPDATE]: [USER_ROLES.OWNER, USER_ROLES.ORG_ADMIN],
      [ACTIONS.DELETE]: [USER_ROLES.OWNER, USER_ROLES.ORG_ADMIN],
      [ACTIONS.GET]: [USER_ROLES.OWNER, USER_ROLES.ORG_ADMIN, USER_ROLES.ORG_READER],
      [ACTIONS.LIST]: [USER_ROLES.OWNER, USER_ROLES.ORG_ADMIN, USER_ROLES.ORG_READER],
    },
    LOCATION_GROUPS: {
      [ACTIONS.CREATE]: [USER_ROLES.OWNER, USER_ROLES.ORG_ADMIN],
      [ACTIONS.UPDATE]: [USER_ROLES.OWNER, USER_ROLES.ORG_ADMIN],
      [ACTIONS.DELETE]: [USER_ROLES.OWNER, USER_ROLES.ORG_ADMIN],
      [ACTIONS.GET]: [USER_ROLES.OWNER, USER_ROLES.ORG_ADMIN, USER_ROLES.ORG_READER],
      [ACTIONS.LIST]: [USER_ROLES.OWNER, USER_ROLES.ORG_ADMIN, USER_ROLES.ORG_READER],
    },
    USERS: {
      [ACTIONS.CREATE]: [USER_ROLES.OWNER, USER_ROLES.ORG_ADMIN],
      [ACTIONS.UPDATE]: [USER_ROLES.OWNER, USER_ROLES.ORG_ADMIN],
      [ACTIONS.DELETE]: [],
      [ACTIONS.GET]: [USER_ROLES.OWNER, USER_ROLES.ORG_ADMIN, USER_ROLES.ORG_READER],
      [ACTIONS.LIST]: [USER_ROLES.OWNER, USER_ROLES.ORG_ADMIN, USER_ROLES.ORG_READER],
    },
    LOCATION_ALERTS: {
      [ACTIONS.CREATE]: [USER_ROLES.OWNER, USER_ROLES.ORG_ADMIN, USER_ROLES.DIV_ADMIN],
      [ACTIONS.UPDATE]: [USER_ROLES.OWNER, USER_ROLES.ORG_ADMIN, USER_ROLES.DIV_ADMIN],
      [ACTIONS.DELETE]: [USER_ROLES.OWNER, USER_ROLES.ORG_ADMIN, USER_ROLES.DIV_ADMIN],
      [ACTIONS.GET]: [
        USER_ROLES.OWNER,
        USER_ROLES.ORG_ADMIN,
        USER_ROLES.ORG_READER,
        USER_ROLES.DIV_ADMIN,
      ],
      [ACTIONS.LIST]: [
        USER_ROLES.OWNER,
        USER_ROLES.ORG_ADMIN,
        USER_ROLES.ORG_READER,
        USER_ROLES.DIV_ADMIN,
      ],
    },
    JOBS: {
      [ACTIONS.CREATE]: [USER_ROLES.OWNER, USER_ROLES.ORG_ADMIN, USER_ROLES.DIV_ADMIN],
      [ACTIONS.UPDATE]: [USER_ROLES.OWNER, USER_ROLES.ORG_ADMIN],
      [ACTIONS.DELETE]: [USER_ROLES.OWNER, USER_ROLES.ORG_ADMIN],
      [ACTIONS.GET]: [USER_ROLES.OWNER, USER_ROLES.ORG_ADMIN, USER_ROLES.ORG_READER],
      [ACTIONS.LIST]: [USER_ROLES.OWNER, USER_ROLES.ORG_ADMIN, USER_ROLES.ORG_READER],
    },
    // Org-wide reference data for listSelect fields; the backend writes only for owner/org admin.
    OPTION_LISTS: {
      [ACTIONS.CREATE]: [USER_ROLES.OWNER, USER_ROLES.ORG_ADMIN],
      [ACTIONS.UPDATE]: [USER_ROLES.OWNER, USER_ROLES.ORG_ADMIN],
      [ACTIONS.DELETE]: [],
      [ACTIONS.GET]: [USER_ROLES.OWNER, USER_ROLES.ORG_ADMIN, USER_ROLES.ORG_READER],
      [ACTIONS.LIST]: [USER_ROLES.OWNER, USER_ROLES.ORG_ADMIN, USER_ROLES.ORG_READER],
    },
    // Org-wide cost centers (GUA-263). Manage = owner/admin/reader; division roles do not enter config.
    COST_CENTERS: {
      [ACTIONS.CREATE]: [USER_ROLES.OWNER, USER_ROLES.ORG_ADMIN, USER_ROLES.ORG_READER],
      [ACTIONS.UPDATE]: [USER_ROLES.OWNER, USER_ROLES.ORG_ADMIN, USER_ROLES.ORG_READER],
      [ACTIONS.DELETE]: [],
      [ACTIONS.GET]: [USER_ROLES.OWNER, USER_ROLES.ORG_ADMIN, USER_ROLES.ORG_READER],
      [ACTIONS.LIST]: [USER_ROLES.OWNER, USER_ROLES.ORG_ADMIN, USER_ROLES.ORG_READER],
    },
    COMPANIES: {
      [ACTIONS.CREATE]: [USER_ROLES.OWNER, USER_ROLES.ORG_ADMIN],
      [ACTIONS.UPDATE]: [USER_ROLES.OWNER, USER_ROLES.ORG_ADMIN],
      [ACTIONS.DELETE]: [],
      [ACTIONS.GET]: [USER_ROLES.OWNER, USER_ROLES.ORG_ADMIN, USER_ROLES.ORG_READER],
      [ACTIONS.LIST]: [USER_ROLES.OWNER, USER_ROLES.ORG_ADMIN, USER_ROLES.ORG_READER],
    },
    WORK_SHIFTS: {
      [ACTIONS.CREATE]: [USER_ROLES.OWNER, USER_ROLES.ORG_ADMIN],
      [ACTIONS.UPDATE]: [USER_ROLES.OWNER, USER_ROLES.ORG_ADMIN],
      [ACTIONS.DELETE]: [USER_ROLES.OWNER, USER_ROLES.ORG_ADMIN],
      [ACTIONS.GET]: [USER_ROLES.OWNER, USER_ROLES.ORG_ADMIN, USER_ROLES.ORG_READER],
      [ACTIONS.LIST]: [USER_ROLES.OWNER, USER_ROLES.ORG_ADMIN, USER_ROLES.ORG_READER],
    },
    WORK_JOURNEY: {
      [ACTIONS.CREATE]: [USER_ROLES.OWNER, USER_ROLES.ORG_ADMIN, USER_ROLES.DIV_ADMIN],
      [ACTIONS.UPDATE]: [USER_ROLES.OWNER, USER_ROLES.ORG_ADMIN],
      [ACTIONS.DELETE]: [USER_ROLES.OWNER, USER_ROLES.ORG_ADMIN],
      [ACTIONS.GET]: [USER_ROLES.OWNER, USER_ROLES.ORG_ADMIN, USER_ROLES.ORG_READER],
      [ACTIONS.LIST]: [USER_ROLES.OWNER, USER_ROLES.ORG_ADMIN, USER_ROLES.ORG_READER],
    },
    ADDRESSES: {
      [ACTIONS.CREATE]: [USER_ROLES.OWNER, USER_ROLES.ORG_ADMIN, USER_ROLES.DIV_ADMIN],
      [ACTIONS.UPDATE]: [USER_ROLES.OWNER, USER_ROLES.ORG_ADMIN],
      [ACTIONS.DELETE]: [USER_ROLES.OWNER, USER_ROLES.ORG_ADMIN],
      [ACTIONS.GET]: [USER_ROLES.OWNER, USER_ROLES.ORG_ADMIN, USER_ROLES.ORG_READER],
      [ACTIONS.LIST]: [USER_ROLES.OWNER, USER_ROLES.ORG_ADMIN, USER_ROLES.ORG_READER],
    },
    HOLIDAYS: {
      [ACTIONS.CREATE]: [USER_ROLES.OWNER, USER_ROLES.ORG_ADMIN],
      [ACTIONS.UPDATE]: [USER_ROLES.OWNER, USER_ROLES.ORG_ADMIN],
      [ACTIONS.DELETE]: [USER_ROLES.OWNER, USER_ROLES.ORG_ADMIN],
      [ACTIONS.GET]: [USER_ROLES.OWNER, USER_ROLES.ORG_ADMIN, USER_ROLES.ORG_READER],
      [ACTIONS.LIST]: [USER_ROLES.OWNER, USER_ROLES.ORG_ADMIN, USER_ROLES.ORG_READER],
    },
    ABSENCES: {
      [ACTIONS.CREATE]: [USER_ROLES.OWNER, USER_ROLES.ORG_ADMIN],
      [ACTIONS.UPDATE]: [USER_ROLES.OWNER, USER_ROLES.ORG_ADMIN],
      [ACTIONS.DELETE]: [USER_ROLES.OWNER, USER_ROLES.ORG_ADMIN],
      [ACTIONS.GET]: [USER_ROLES.OWNER, USER_ROLES.ORG_ADMIN, USER_ROLES.ORG_READER],
      [ACTIONS.LIST]: [USER_ROLES.OWNER, USER_ROLES.ORG_ADMIN, USER_ROLES.ORG_READER],
    },
  },
  USERS: {
    [ACTIONS.CREATE]: [],
    [ACTIONS.UPDATE]: [],
    [ACTIONS.DELETE]: [],
    [ACTIONS.GET]: [],
    [ACTIONS.LIST]: [],
  },
  USERS_ACCESS: {
    [ACTIONS.CREATE]: [],
    [ACTIONS.UPDATE]: [],
    [ACTIONS.DELETE]: [],
    [ACTIONS.GET]: [],
    [ACTIONS.LIST]: [],
  },
};

registerWriteRoleLists(ALLOWED_ROLES, [ORGANIZATION_CREATE_ROLES]);

/**
 * Whether JWT org claims allow reading the Firestore organizations/{organizationId} document.
 * Keeps the org-switch flow aligned with the initial load guard in appRedux/sagas/user/access.js.
 * When the token is packed (pk flag) and the org entry is missing, the optional
 * `fallbackOrgAccess` (from the org user doc) is used instead.
 */
export const canGetOrganizationDocument = (
  claims: (ClaimsWithOrg & { pk?: number }) | null | undefined,
  organizationId: string,
  fallbackOrgAccess?: string,
): boolean => {
  const userOrgAccess = resolveOrgAccess(claims, organizationId, fallbackOrgAccess);
  return hasAnyAllowedRole(ALLOWED_ROLES.ORGANIZATIONS.GET as AllowedRoles, userOrgAccess);
};

/**
 * Organization-level role select options (never includes division-only EDITOR).
 * @param {boolean} includeOwnerRoleOption - When true (viewer is org owner), OWNER is included so another owner can be assigned.
 */
export const buildOrganizationAccessOptions = (
  includeOwnerRoleOption: boolean,
): { label: string; value: string }[] => {
  const keys = Object.values(ACCESS_TYPES).filter(
    key => key !== ACCESS_TYPES.OWNER && key !== ACCESS_TYPES.EDITOR,
  );
  const keysWithOptionalOwner = includeOwnerRoleOption ? [...keys, ACCESS_TYPES.OWNER] : keys;

  return keysWithOptionalOwner
    .map(key => ({
      label: ACCESS_TYPES_INTL[key]?.name,
      value: key,
    }))
    .filter(opt => Boolean(opt.label))
    .sort((a, b) => (a.label.toLocaleLowerCase() > b.label.toLocaleLowerCase() ? 1 : -1));
};

export const organizationAccessOptions = buildOrganizationAccessOptions(false);
